import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import PackOpenAnimatorContainer from '../../features/shop/containers/packOpenAnimatorContainer';

import './style.css';

const CARD_IMAGE_BASE = 'https://en.onepiece-cardgame.com/images/cardlist/card';

const mockCard = (id, name, rarity, type = 'Character') => ({
    id,
    name,
    rarity,
    type,
    imgUrl: `${CARD_IMAGE_BASE}/${id}.png`,
});

const CARD_POOL = {
    C: [
        mockCard('OP01-007', 'Caribou', 'C'),
        mockCard('OP01-008', 'Cavendish', 'C'),
        mockCard('OP01-009', 'Carrot', 'C'),
        mockCard('OP01-010', 'Komachiyo', 'C'),
        mockCard('OP01-012', 'Sai', 'C'),
        mockCard('OP01-019', 'Bartolomeo', 'C'),
    ],
    UC: [
        mockCard('OP01-006', 'Otama', 'UC'),
        mockCard('OP01-011', 'Gordon', 'UC'),
        mockCard('OP01-014', 'Jinbe', 'UC'),
        mockCard('OP01-021', 'Franky', 'UC'),
    ],
    R: [
        mockCard('OP01-004', 'Usopp', 'R'),
        mockCard('OP01-013', 'Sanji', 'R'),
        mockCard('OP01-016', 'Nami', 'R'),
        mockCard('OP01-017', 'Nico Robin', 'R'),
    ],
    SR: [
        mockCard('OP01-024', 'Monkey.D.Luffy', 'SR'),
        mockCard('OP01-025_p1', 'Roronoa Zoro', 'SR-Alt'),
    ],
    L: [
        mockCard('OP01-001', 'Roronoa Zoro', 'L', 'Leader'),
        mockCard('OP01-031', 'Kozuki Oden', 'L', 'Leader'),
    ],
    SEC: [
        mockCard('OP01-120', 'Shanks', 'SEC'),
        mockCard('OP01-121', 'Yamato', 'SEC'),
    ],
};

const PRESETS = {
    typical: { label: 'Typical pull (4C, 1UC, 1R)', rarities: ['C', 'C', 'C', 'C', 'UC', 'R'] },
    lucky: { label: 'Lucky pull (SR hit)', rarities: ['C', 'C', 'UC', 'R', 'R', 'SR'] },
    jackpot: { label: 'Jackpot (SEC + leader)', rarities: ['C', 'UC', 'R', 'L', 'SR', 'SEC'] },
    allCommons: { label: 'All commons', rarities: ['C', 'C', 'C', 'C', 'C', 'C'] },
    single: { label: 'Single SEC card', rarities: ['SEC'] },
    twelve: { label: 'Twelve cards', rarities: ['C', 'C', 'C', 'C', 'UC', 'UC', 'R', 'R', 'SR', 'L', 'SEC', 'C'] },
};

const pickCard = (rarity, index) => {
    const pool = CARD_POOL[rarity];
    return pool[index % pool.length];
};

const buildPackOpenResult = (presetKey) => ({
    id: 'pack-OP01',
    type: 'pack',
    packId: 'OP01',
    name: 'OP-01 Romance Dawn Pack',
    price: 100,
    claimCount: 1,
    coins: 400,
    openedCards: PRESETS[presetKey].rarities.map((rarity, index) => ({
        ...pickCard(rarity, index),
        isNew: index === 0 || !['C', 'UC'].includes(rarity),
    })),
});

const PackOpenDemoPage = () => {
    const { app, setAutoResize } = usePixiApp();
    const pixiContainerRef = useRef(null);
    const animatorRef = useRef(null);
    const runIdRef = useRef(0);

    const [presetKey, setPresetKey] = useState('typical');
    const [isLoading, setIsLoading] = useState(false);
    const [collectedAt, setCollectedAt] = useState(null);

    const startAnimator = useCallback(async () => {
        if (!app || !pixiContainerRef.current) return;

        const runId = ++runIdRef.current;
        animatorRef.current?.detachFromPixiApp(app);
        animatorRef.current?.destroy({ children: true });
        animatorRef.current = null;
        setCollectedAt(null);
        setIsLoading(true);

        const animator = new PackOpenAnimatorContainer(buildPackOpenResult(presetKey), {
            width: app.screen.width,
            height: app.screen.height,
        });
        animator.onCollect = () => setCollectedAt(new Date().toLocaleTimeString());

        await animator.loadAssets();
        if (runId !== runIdRef.current) {
            animator.destroy({ children: true });
            return;
        }

        animator.attachToPixiApp(app);

        if (!pixiContainerRef.current.contains(app.canvas)) {
            pixiContainerRef.current.appendChild(app.canvas);
        }

        animatorRef.current = animator;
        setIsLoading(false);
        await animator.start();
    }, [app, presetKey]);

    useEffect(() => {
        setAutoResize(true);
        startAnimator();

        return () => {
            if (animatorRef.current && app) {
                animatorRef.current.detachFromPixiApp(app);
                animatorRef.current.destroy({ children: true });
                animatorRef.current = null;
            }
            setAutoResize(false);
        };
    }, [app, startAnimator, setAutoResize]);

    useEffect(() => {
        if (!app) return;

        const onAppResize = () => {
            animatorRef.current?.resize(app.screen.width, app.screen.height);
        };

        window.addEventListener('appResize', onAppResize);

        return () => {
            window.removeEventListener('appResize', onAppResize);
        };
    }, [app]);

    return (
        <div className="pack-open-demo">
            <div className="pack-open-demo__controls">
                <h1 style={{ margin: '0 0 12px', fontSize: 18 }}>Pack Open Demo</h1>

                <label style={{ display: 'block', marginBottom: 12 }}>
                    Pull preset
                    <select
                        value={presetKey}
                        onChange={(e) => setPresetKey(e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 4 }}
                    >
                        {Object.entries(PRESETS).map(([key, preset]) => (
                            <option key={key} value={key}>{preset.label}</option>
                        ))}
                    </select>
                </label>

                <button type="button" onClick={startAnimator} disabled={isLoading}>
                    {isLoading ? 'Loading assets...' : 'Replay pack open'}
                </button>

                <p style={{ marginTop: 16, color: '#555' }}>
                    Tap the pack to rip it open, then tap a card to slide it off the stack.
                    Cards marked new get a NEW badge after they are revealed.
                    Rare pulls play the flourish from the <Link to="/animation-editor">animation editor</Link>.
                </p>

                {collectedAt && <p style={{ color: '#2b7a2b' }}>onCollect fired at {collectedAt}</p>}
            </div>
            <div className="pack-open-demo__canvas" ref={pixiContainerRef} />
        </div>
    );
};

export default PackOpenDemoPage;
